import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useMembership,
  useMembershipDelete,
  useMembershipDisable,
  useMembershipEnable,
  useMembershipList,
  useMutationMembershipEdit,
} from "@gymflow/api";
import { ServiceType } from "@gymflow/common";
import { formatCurrency, pluralize } from "@gymflow/helpers";
import { MembershipBean, MembershipType } from "@gymflow/types";
import { createColumnHelper, SortingState } from "@tanstack/react-table";
import { copyToClipboard } from "apps/portal/src/helpers";
import { usePageSize, usePortalRoutes } from "apps/portal/src/hooks";
import { RouteFeature, RouteLayout } from "apps/portal/src/routes";
import classNames from "classnames";
import { format, parse } from "date-fns";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ModalContext, useClubSettings } from "../../../providers";
import useGymflowModels from "../../../store";
import {
  Button,
  CopyIcon,
  PaginatedSelect,
  ReactiveButton,
  Switch,
} from "../../atoms";
import { PaginatedTable } from "../../organisms/PaginatedTable";
import { ConfirmModal } from "../../templates";
import { SettingsContainer } from "../SettingsContainer";
import { PrepaidMembershipFormSidebarProviderContext } from "./PrepaidMembershipFormSidebarProvider";
import { RecurringMembershipFormSidebarProviderContext } from "./RecurringMembershipFormSidebarProvider";
import { TrialMembershipFormSidebarProviderContext } from "./TrialMembershipFormSidebarProvider";

const tabs = ["recurring", "prepaid", "trial"] as const;
const typeToString: { [key in (typeof tabs)[number]]: string } = {
  recurring: "Recurring",
  prepaid: "Pre-Paid",
  trial: "Trial",
};
const filters: {
  [key in (typeof tabs)[number]]: {
    isTrial?: boolean;
    type: MembershipType;
  };
} = {
  recurring: { type: "RECURRING" },
  prepaid: { isTrial: false, type: "PREPAID" },
  trial: { isTrial: true, type: "PREPAID" },
};
export function MembershipsTab() {
  const [currentPage, setCurrentPage] = useState(0);
  const [showingStatus, setShowingStatus] = useState<"ACTIVE" | "INACTIVE">(
    "ACTIVE",
  );
  const [showingType, setShowingType] =
    useState<(typeof tabs)[number]>("recurring");
  const [sort, setSort] = useState<SortingState>([]);

  const { api } = useGymflowModels();

  const { club } = useClubSettings();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const pageSize = usePageSize({
    tableContainerRef,
    rowHeight: 56,
  });

  const { data, isLoading } = useMembershipList({
    api,
    opts: {
      page: currentPage,
      limit: pageSize,
      sort: sort?.[0]
        ? {
            field: sort[0].id,
            desc: sort[0].desc,
          }
        : { field: "createdDate", desc: true },
      extraParams: {
        status: showingStatus,
        ...filters[showingType],
      },
    },
  });

  const [editingMembershipId, setEditingMembershipId] = useState<number>();

  const { data: editing } = useMembership({
    api,
    membershipId: editingMembershipId,
  });
  const { mutateAsync: editMembership } = useMutationMembershipEdit({
    api,
  });
  const { mutateAsync: enableMembership } = useMembershipEnable({
    api,
  });
  const { mutateAsync: disableMembership } = useMembershipDisable({
    api,
  });
  const { mutateAsync: deleteMembership } = useMembershipDelete({
    api,
  });

  const { showPrepaidMembershipForm: triggerPrepaidMembershipForm } =
    useContext(PrepaidMembershipFormSidebarProviderContext);
  const { showRecurringMembershipForm: triggerRecurringMembershipForm } =
    useContext(RecurringMembershipFormSidebarProviderContext);
  const { showTrialMembershipForm: triggerTrialMembershipForm } = useContext(
    TrialMembershipFormSidebarProviderContext,
  );

  const showAlert = useCallback(() => {
    if (showingType === "recurring") {
      triggerRecurringMembershipForm({
        currentRecurringMembership: (editing ?? undefined) as
          | MembershipBean
          | undefined,
        onClose: () => {
          setEditingMembershipId(undefined);
        },
      });
    } else if (showingType === "prepaid") {
      triggerPrepaidMembershipForm({
        currentPrepaidMembership: (editing ?? undefined) as
          | MembershipBean
          | undefined,
        onClose: () => {
          setEditingMembershipId(undefined);
        },
      });
    } else if (showingType === "trial") {
      triggerTrialMembershipForm({
        currentTrialMembership: (editing ?? undefined) as
          | MembershipBean
          | undefined,
        onClose: () => {
          setEditingMembershipId(undefined);
        },
      });
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showingType,
    triggerRecurringMembershipForm,
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(editing),
    triggerPrepaidMembershipForm,
    triggerTrialMembershipForm,
  ]);

  useEffect(() => {
    if (editingMembershipId) {
      showAlert();
    }
  }, [editingMembershipId, showAlert]);
  const { createAbsoluteClubLink } = usePortalRoutes();
  const { setModal, hide } = useContext(ModalContext);
  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<MembershipBean>();
    return [
      columnHelper.accessor("name", {}),
      columnHelper.accessor(
        (e) => ({
          type: e.type,
          nextMonthlyPayment: e.nextMonthlyPayment,
          defaultPrice: e.defaultPrice,
        }),
        {
          id: "defaultPrice",
          header: "Price",
          cell: (column) => {
            const value = column.getValue();
            return formatCurrency(
              value.type === ServiceType.Recurring
                ? value.nextMonthlyPayment
                : value.defaultPrice,
              club.defaultCurrency,
            );
          },
        },
      ),
      ...(showingType === "recurring"
        ? [
            columnHelper.accessor(
              (e) => ({
                billingPeriod: e.billingPeriod,
                billingType: e.billingType,
              }),
              {
                id: "billingType,billingPeriod",
                header: () => <div>Billing Period</div>,
                cell: (column) => {
                  const value = column.getValue();
                  return `Every ${value.billingPeriod} ${pluralize(
                    { MONTHLY: "Month", WEEKLY: "Week" }[
                      value?.billingType ?? "MONTHLY"
                    ],
                    { MONTHLY: "Months", WEEKLY: "Weeks" }[
                      value?.billingType ?? "MONTHLY"
                    ],
                    value.billingPeriod,
                  )}`;
                },
                enableSorting: false,
              },
            ),
            columnHelper.accessor(
              (e) => ({
                monthlyBillingDay: e.monthlyBillingDay,
                weeklyBillingDay: e.weeklyBillingDay,
                billingType: e.billingType,
                calculateProrata: e.calculateProrata,
                container: e,
              }),
              {
                id: "billingDay",
                header: () => <div>Bills On</div>,
                cell: (column) => {
                  const value = column.getValue();
                  return value.billingType === "MONTHLY"
                    ? value.calculateProrata === "NO"
                      ? "Start Date"
                      : `${format(
                          parse(`${value.monthlyBillingDay}`, "d", new Date()),
                          "do",
                        )} of the Month`
                    : value.calculateProrata === "NO"
                      ? "Start Day"
                      : `${value.weeklyBillingDay
                          ?.split("")
                          .map((e, i) =>
                            i === 0 ? e.toUpperCase() : e.toLocaleLowerCase(),
                          )
                          .join("")}s`;
                },
                enableSorting: false,
              },
            ),
          ]
        : []),
      ...(showingType === "prepaid" || showingType === "trial"
        ? [
            columnHelper.accessor(
              (e) => ({
                duration: e.duration,
                durationType: e.durationType,
              }),
              {
                id: "duration",
                header: () => <div>Length</div>,
                cell: (column) => {
                  const value = column.getValue();
                  return `${value.duration} ${pluralize(
                    {
                      DAILY: "Day",
                      WEEKLY: "Week",
                      MONTHLY: "Month",
                      NO_CONTRACT: "None",
                    }[value.durationType ?? "NO_CONTRACT"],
                    {
                      DAILY: "Days",
                      WEEKLY: "Weeks",
                      MONTHLY: "Months",
                      NO_CONTRACT: "None",
                    }[value.durationType ?? "NO_CONTRACT"],
                    value.duration,
                  )}`;
                },
                enableSorting: false,
              },
            ),
          ]
        : []),

      columnHelper.accessor(
        (e) => ({
          isPublic: e.isPublic,
          id: e.id,
        }),
        {
          id: "isPublic",
          header: () => <div>Available Online</div>,
          cell: (column) => {
            const value = column.getValue();
            return (
              <Switch
                value={!!value.isPublic}
                onChange={async (newValue) => {
                  await editMembership({
                    membershipId: value.id,
                    patchedFields: {
                      isPublic: newValue,
                    },
                  });
                }}
              />
            );
          },
        },
      ),
      columnHelper.accessor("id", {
        id: "membershipLink",
        header: "Membership Link",
        cell: (column) => (
          <ReactiveButton
            size="small"
            onClick={async () => {
              await copyToClipboard(
                createAbsoluteClubLink(
                  RouteLayout.Site,
                  RouteFeature.SiteBuyMembershipId.replace(
                    ":membershipType",
                    "all",
                  ).replace(":membershipId", column.getValue().toString()),
                ),
              );
            }}
            reactiveContent="Link Copied"
            className="mr-4 w-32"
            disabled={!column.row.original.isPublic}
            tooltip={
              column.row.original.isPublic
                ? undefined
                : "To enable the link the membership should be available online"
            }
          >
            <div className="flex w-full items-center gap-1">
              <CopyIcon pathClassName="stroke-gray-500" />
              Copy Link
            </div>
          </ReactiveButton>
        ),
      }),
      columnHelper.accessor("id", {
        header: () => <div>Actions</div>,
        cell: (column) => {
          const value = column.getValue();
          return (
            <div className="flex flex-row items-center gap-x-2">
              <div
                className="text-primary-700 cursor-pointer text-sm font-semibold"
                onClick={() => {
                  setEditingMembershipId(value);
                }}
              >
                Edit
              </div>
              <div
                className="text-warning-600 cursor-pointer text-sm font-semibold"
                onClick={async () => {
                  if (showingStatus === "ACTIVE") {
                    await disableMembership(value);
                  } else {
                    await enableMembership(value);
                  }
                }}
              >
                {showingStatus === "ACTIVE" ? "Disable" : "Enable"}
              </div>
              <div
                className="text-error-500 cursor-pointer text-sm font-semibold"
                onClick={() => {
                  setModal(
                    <ConfirmModal
                      title="Delete Membership"
                      type="danger"
                      onConfirm={async () => {
                        await deleteMembership(value);

                        hide();
                      }}
                      onCancel={() => {
                        hide();
                      }}
                    >
                      Are you sure you want to delete this membership?
                    </ConfirmModal>,
                  );
                }}
              >
                Delete
              </div>
            </div>
          );
        },
        enableSorting: false,
      }),
    ];
  }, [
    createAbsoluteClubLink,
    deleteMembership,
    disableMembership,
    editMembership,
    enableMembership,
    hide,
    setModal,
    club.defaultCurrency,
    showingStatus,
    showingType,
  ]);

  return (
    <SettingsContainer
      title="Memberships"
      subTitle="Create, Update and manage all your different membership types."
      actions={
        <>
          <PaginatedSelect
            className="bg-gray-0 h-11"
            placeholder="Enabled"
            value={[
              {
                label: "Enabled",
                value: "ACTIVE",
              },
              {
                label: "Disabled",
                value: "INACTIVE",
              },
            ].find((e) => e.value === showingStatus)}
            onChange={({ value }) => setShowingStatus(value)}
            loadOptions={async () => {
              return {
                options: [
                  {
                    label: "Enabled",
                    value: "ACTIVE",
                  },
                  {
                    label: "Disabled",
                    value: "INACTIVE",
                  },
                ],
              };
            }}
          />
          <Button
            intent="secondary"
            className="text-gray-0"
            onClick={() => {
              showAlert();
            }}
          >
            <div className="flex flex-row items-center gap-x-2">
              <FontAwesomeIcon icon={faPlus} />
              <div>{typeToString[showingType]}</div>
            </div>
          </Button>
        </>
      }
    >
      <div className="flex flex-row gap-x-2 border-b border-gray-200">
        {tabs.map((e) => (
          <div
            key={e}
            onClick={() => setShowingType(e)}
            className={classNames(
              "max-w-min cursor-pointer flex-nowrap whitespace-nowrap px-1 pb-3 text-sm font-semibold text-gray-500",
              {
                "text-secondary-500 border-secondary-500 border-b-2":
                  showingType === e,
              },
            )}
          >
            {typeToString[e]}
          </div>
        ))}
      </div>
      <PaginatedTable
        tableProps={{
          data: data?.content ?? [],
          columns: columns,
          pageCount: data?.totalPages,
          pageIndex: data?.number,
          onSortingChange: setSort,
          sort: sort,
          isFetching: isLoading,
          pageSize: pageSize,
          tableContainerRef,
        }}
        hasNextPage={!!data && data?.number < data?.totalPages - 1}
        hasPreviousPage={!!data && data?.number > 0}
        goToNextPage={() => {
          setCurrentPage(currentPage + 1);
        }}
        goToPreviousPage={() => {
          setCurrentPage(currentPage - 1);
        }}
      />
    </SettingsContainer>
  );
}
